import {
  Component,
  Input,
  OnInit,
  Output,
  ViewChild,
  EventEmitter,
  AfterViewInit,
  inject,
} from '@angular/core';
import { State } from 'src/app/shared/state/state';
import { closest } from '@syncfusion/ej2-base';
import { DragAndDropEventArgs } from '@syncfusion/ej2-navigations';
import {
  TreeViewComponent,
  TreeViewModule,
} from '@syncfusion/ej2-angular-navigations';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SuggestedSearchPipe } from 'src/app/shared/pipes/suggested-search.pipe';
import { InombraRoutes } from 'src/app/models/routes';
import { DeleteEventModalComponent } from '../delete-modal/delete-modal.component';
import { EditorEventModalComponent } from '../editor-modal/editor-modal.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { SchedulerService } from 'src/app/shared/services/scheduler.service';
import moment from 'moment';
import { Subject, finalize, switchMap } from 'rxjs';
import { UploadSources } from 'src/app/modules/private/file-manager/models';
import { currentTimeZone, timeZone } from 'src/app/app.constants';
import { Utils } from 'src/app/shared/services/utilities';
import { SortByPipe } from 'src/app/shared/pipes/sort-by.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { EventHttpService } from 'src/app/shared/services/http/event-http.service';
import { CommonModule } from '@angular/common';
import {
  ScheduleModule,
  ScheduleAllModule,
  ScheduleComponent,
} from '@syncfusion/ej2-angular-schedule';
import { WeekViewEventComponent } from '../week-view-event/week-view-event.component';
import { MonthViewEventComponent } from '../month-view-event/month-view-event.component';
import { ModuleTagComponent } from 'src/app/shared/components/module-tag/module-tag.component';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { CalendarLegendComponent } from '../legend/legend.component';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { EventInfoComponent } from '../event-info/event-info.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DayViewEventComponent } from '../day-view-event/day-view-event.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { DueDateTagComponent } from 'src/app/shared/components/due-date-tag/due-date-tag.component';
import { DueDateComparisonTagsComponent } from 'src/app/shared/components/due-date-comparison-tags/due-date-comparison-tags.component';
import { EventCalendar } from 'src/app/models';

@UntilDestroy()
@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DragDropModule,
    TreeViewModule,
    ScheduleModule,
    ScheduleAllModule,
    DayViewEventComponent,
    WeekViewEventComponent,
    MonthViewEventComponent,
    EventInfoComponent,
    CalendarLegendComponent,
    ModuleTagComponent,
    DueDateTagComponent,
    DueDateComparisonTagsComponent,
    NzIconModule,
    NzButtonModule,
    NzDropDownModule,
    NzToolTipModule,
    NzEmptyModule,
  ],
})
export class CalendarComponent implements OnInit, AfterViewInit {
  readonly state = inject(State);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly window = inject(Window);
  readonly modal = inject(NzModalService);
  readonly utilities = inject(Utils);
  readonly notify = inject(NotificationService);
  readonly schedulerService = inject(SchedulerService);
  readonly eventHttp = inject(EventHttpService);
  @ViewChild('scheduleObj') scheduleObj: ScheduleComponent & {
    requestEvents: Function;
    updateEvents: Function;
  };
  @Input() view = 'Week';
  @Input() readonly: boolean = false;
  @Input() showWaitingTasks: boolean = true;
  @Output() dateChanged = new EventEmitter();
  @Output() cellClick = new EventEmitter();
  @Input() selectedDate = moment().format('YYYY-MM-DD');
  @Input() preselectedProject = null;
  @Input() allowLegend = true;
  @Input() showHeaderBar = true;
  @Input() technicians = [];
  @Input() blockEvents = false;
  readonly UploadSources = UploadSources;
  readonly timeZone = timeZone;
  readonly currentTimeZone = currentTimeZone;
  readonly InombraRoutes = InombraRoutes;
  public setViews = ['Day', 'Week', 'Month'];
  public searchTaskValue = '';

  public eventSettings = {
    fields: {
      subject: {
        title: 'Event Name',
        name: 'Subject',
        default: '',
      },
    },
    enableTooltip: true,
    tooltipTemplate: this.tooltipTemplate,
  };

  get tooltipTemplate() {
    let template =
      '<div class="tooltip-wrap"><div class="content-area">' +
      '<div class="eventTime">${StartTime.toLocaleTimeString()}  - ${EndTime.toLocaleTimeString()} </div>' +
      '<div class="eventPostCode">${postcode}</div>' +
      '<div class="eventDescription">${Subject}</div>' +
      '<div>${business}</div>' +
      '<div>${customerCompanyName}</div>' +
      '<div>${projectStatus} - ${financialStatus}</div>' +
      '<div class="eventBilling"><nz-icon nzType="pound" nzTheme="outline" />${billingContactName}</div>' +
      '<div class="eventDelivery">${deliveryName}: ${deliveryPhoneLandline} ${deliveryPhoneMobile}</div>' +
      '<div class="eventAddress">${address1} ${address2}, ${postcode}</div>' +
      '<div class="eventType">${type}: ${ProjectId}</div>' +
      '<div class="eventTechnician">${TechnicianNames}</div>' +
      '</div></div>';
    return template;
  }

  get masterCustomerId() {
    return this.state?.customer?.id?.toString();
  }

  @ViewChild('treeObj')
  public treeObj: TreeViewComponent;
  public field;
  public isTreeItemDropped: boolean = false;
  private updateEvents$ = new Subject<Event>();

  constructor() {
    this.verifiyRouteParams();

    this.schedulerService.requestEvents$
      .pipe(switchMap(() => this.scheduleObj.requestEvents()))
      .subscribe((data: EventCalendar[]) => {
        this.scheduleObj.hideSpinner();
        this.scheduleObj.eventSettings.dataSource = data as Record<
          string,
          any
        >[];

        this.showWeekendIfEventExists(data);
        this.openEventFromURL(data);
      });

    this.updateEvents$
      .pipe(switchMap(event => this.scheduleObj.updateEvents(event)))
      .subscribe(() => this.schedulerService.requestEvents$.next());
  }

  showWeekendIfEventExists(events: EventCalendar[]): void {
    if (events?.length) {
      const someOnWeekend = events.some(
        event =>
          moment(event.EndTime).isoWeekday() >= 6 ||
          moment(event.StartTime).isoWeekday() >= 6
      );
      this.schedulerService.showWeekend.set(someOnWeekend ? true : false);
    }
  }

  openEventFromURL(events: EventCalendar[]): void {
    if (events?.length) {
      if (this.openEventById) {
        const targetEvent = events.find(e => e.Id == this.openEventById);
        if (targetEvent) {
          this.editorModal({ ...targetEvent, Guid: 'temp' });
        }
        this.router.navigate([InombraRoutes.Calendar], {
          replaceUrl: true,
        });
      }
    }
  }

  ngAfterViewInit(): void {
    this.scheduleObj.requestEvents = () => {
      this.scheduleObj.showSpinner();
      const dates = this.scheduleObj.activeView.renderDates;
      const showWeekend = this.schedulerService.showWeekend();
      const selectedTechniciansIds = this.selectedTechnicians.join(',');
      const params = {
        StartDate: moment(dates[0]).clone().startOf('day').toDate(),
        EndDate: moment(dates[dates.length - 1])
          .clone()
          .add(showWeekend ? 0 : 2, 'day')
          .endOf('day')
          .toDate(),
      };
      if (this.filterEventType) {
        params['filterEventType'] = this.filterEventType;
      }
      if (selectedTechniciansIds) {
        params['technicians'] = selectedTechniciansIds;
      }
      return this.eventHttp.getCalendarEvents({ params, ...params });
    };

    this.scheduleObj.updateEvents = event => {
      event.StartTime = moment(event.StartTime).toDate();
      event.EndTime = moment(event.EndTime).toDate();
      let payload = {
        action: 'batch',
        added: [],
        changed: [event],
        deleted: [],
        params: {},
      };
      return this.eventHttp.updateCalendarEvents(payload).pipe(
        finalize(() => {
          this.notify.info(
            'Calendar Updated',
            'Your changes have been saved successfully.'
          );
        })
      );
    };
  }

  openEventById = null;
  verifiyRouteParams() {
    this.route.params.pipe(untilDestroyed(this)).subscribe(params => {
      if (params['openEventById']) {
        this.openEventById = params['openEventById'];
      }
    });
  }

  onActionComplete($event): void {
    const getEventsFor = ['dateNavigate', 'viewNavigate'];
    if (getEventsFor.includes($event.requestType)) {
      this.schedulerService.requestEvents$.next();
    }
  }

  onCreate(): void {
    this.schedulerService.requestEvents$.next();
  }

  selectedTechnicians = [];
  processFilterOutput(filter) {
    this.selectedTechnicians = filter;
    this.schedulerService.requestEvents$.next();
  }

  filterEventType = [];
  legendFilterChanged(types): void {
    this.filterEventType = types;
    this.schedulerService.requestEvents$.next();
  }

  ngOnInit(): void {
    this.state.observableCustomer.subscribe(() => this.reRenderEvents());
    this.schedulerService.showCustomEvents$.subscribe(() =>
      this.reRenderEvents(false)
    );
    this.schedulerService.technicians$.subscribe(
      this.processFilterOutput.bind(this)
    );
  }

  reRenderEvents(fetchWaitingTasks = true) {
    if (this.scheduleObj) {
      const events = this.scheduleObj.getEvents();
      events.forEach((data, index) => {
        const element = document.getElementsByClassName('e-appointment')[index];
        this.scheduleObj.trigger('eventRendered', { data, element });
      });
      if (fetchWaitingTasks) {
        this.fetchWaitingTasks();
      }
    }
  }

  getItemDescription(item) {
    switch (item.type) {
      case 'account':
        return item.account_description;
      case 'workflow':
        return item.description;
      case 'cart':
        return item.description;
      default:
        return '';
    }
  }

  waitingTasks = {
    hidden: (() => {
      let ls = localStorage.getItem('hiddenWaitingTasks');
      return ls || false;
    })(),
    toggle: () => {
      this.waitingTasks.hidden = !this.waitingTasks.hidden;
      this.selectedDueDate('all');
      if (this.waitingTasks.hidden) {
        localStorage.setItem('hiddenWaitingTasks', 'true');
      } else {
        localStorage.removeItem('hiddenWaitingTasks');
      }
    },
    data: null,
    set: function (arg) {
      this.data = {
        dataSource: arg,
        id: 'id',
        text: 'text',
      };
    },
    search: false,
  };

  sortTasksBy(field, direction): void {
    const result = new SortByPipe().transform(
      this.waitingTasksData,
      field,
      direction
    );
    this.treeObj.fields = {
      dataSource: result,
      id: 'id',
      text: field + '-' + direction,
    };
    this.treeObj.dataBind();
  }

  searchTask(event) {
    if (event)
      this.waitingTasks.set(
        new SuggestedSearchPipe().transform(
          this.waitingTasksData,
          ['search'],
          event
        )
      );
    else this.waitingTasks.set(this.waitingTasksData);
  }

  loadingTasks = true;
  waitingTasksData = [];
  waitingTasksInsights: any = {};
  async fetchWaitingTasks() {
    this.loadingTasks = true;
    this.waitingTasksData = [];

    const items = await this.schedulerService.waitingProjects({
      action: '"book":true',
      customer: this.masterCustomerId,
    });

    items.forEach(item => {
      let task = {
        id: item.id,
        status: item.project_status,
        tag_color:
          this.state.projectStatus.find(i => i.id == item.project_status_id)
            ?.color || 'var(--primary-color)',
        postcode: item.delivery_postcode,
        address: item.address_id,
        address1: item.delivery_address1,
        address2: item.delivery_address2,
        billing_name: item.delivery_name,
        city: item.delivery_city,
        type: item.type,
        reference: item.alias + '-' + item.id,
        description: this.getItemDescription(item),
        due_date: item.due_date,
        date_diff: this.utilities.dateDiff(item.due_date, 'days'),
        show: true,
        search:
          item.delivery_name +
          ' ' +
          item.delivery_city +
          ' ' +
          item.delivery_address2 +
          ' ' +
          item.delivery_address1 +
          ' ' +
          item.delivery_postcode,
      };
      task['externalLink'] = this.externalLink(task);
      this.waitingTasksData.push(task);
    });

    this.waitingTasksInsights = {
      duetoday: this.waitingTasksData.filter(i => i.date_diff == 0).length,
      overdue: this.waitingTasksData.filter(i => i.date_diff > 0).length,
    };

    this.waitingTasks.set(this.waitingTasksData);
    this.loadingTasks = false;
  }

  selectedDueDate(event: string): void {
    this.waitingTasks.set(
      this.waitingTasksData.reduce((acc, task) => {
        if (event == 'overdue') {
          task.show = task.date_diff > 0;
        } else if (event == 'today') {
          task.show = task.date_diff == 0;
        } else {
          task.show = true;
        }

        if (task.show) {
          acc.push(task);
        }
        return acc;
      }, [])
    );
  }

  externalLink(data) {
    return this.router.serializeUrl(
      this.router.createUrlTree([`/${data.type}/${data.id}`])
    );
  }

  openExternalLink(url) {
    this.window.open(url, '_blank');
  }

  deleteModal(event) {
    this.modal
      .create({
        nzContent: DeleteEventModalComponent,
        nzClosable: false,
        nzMaskClosable: false,
        nzCentered: true,
        nzData: event,
      })
      .afterClose.subscribe(result => {
        if (result) {
          this.notify.info(
            'Calendar Event Removed',
            'Your changes have been saved successfully.'
          );
          this.schedulerService.requestEvents$.next();
        }
      });
  }

  editorModal(event) {
    this.modal
      .create({
        nzContent: EditorEventModalComponent,
        nzClosable: false,
        nzMaskClosable: false,
        nzCentered: true,
        nzWidth: 624,
        nzData: {
          params: event,
          preselectedProject: this.preselectedProject,
        },
      })
      .afterClose.subscribe(result => {
        if (result) {
          this.notify.info(
            'Calendar Updated',
            'Your changes have been saved successfully.'
          );
          this.schedulerService.requestEvents$.next();
          if (result.type !== 'custom') {
            this.fetchWaitingTasks();
          }
        }
      });
  }

  dragPosition = { x: 0, y: 0 };
  onPopupOpen(args): void {
    if (this.blockEvents) {
      args.cancel = true;
    } else {
      if (args.type === 'Editor') {
        this.editorModal(args.data);
        args.cancel = true;
      }
      if (args.type === 'DeleteAlert') {
        this.deleteModal(args.data);
        args.cancel = true;
      }
      if (args.type === 'QuickInfo') {
        (args.element as HTMLElement).style.transform = '';
        this.dragPosition = { x: 0, y: 0 };
      }
    }
  }

  onDataBound(): void {
    this.dateChanged.emit(this.selectedDate);
  }

  strippedBackground(event): string {
    const technicians = this.state.technicians.filter(i =>
      event.data['TechnicianId'].includes(i.id)
    );
    const percentage = 100 / technicians.length;
    let actualPercentage = 0;

    let background = 'repeating-linear-gradient(to right';
    technicians.forEach(({ colors }) => {
      let initialPercentage = actualPercentage;
      actualPercentage = actualPercentage + percentage;
      background +=
        ', ' +
        colors.primary +
        ' ' +
        initialPercentage +
        '%, ' +
        colors.primary +
        ' ' +
        actualPercentage +
        '% ';
    });
    background += ')';

    return background;
  }

  onEventRendered(event): void {
    const showCustomEvents = this.schedulerService.showCustomEvents$.getValue();
    const isCustomEvent = event.data.type === 'custom' || event.data.IsReminder;

    let technician = this.state.technicians.find(
      i => i.id == event.data.TechnicianId[0]
    );
    let backgroundColor = technician?.colors.primary;

    if (event.data.greyed) {
      event.element.style.background =
        'repeating-linear-gradient(45deg, #999999, #999999 10px, #888888 10px, #888888 20px)';
    } else {
      if (event.element && backgroundColor) {
        if (this.scheduleObj.currentView === 'Agenda') {
          (event.element.firstChild as HTMLElement).style.borderLeftColor =
            backgroundColor;
        } else {
          if (event.data.TechnicianId.length == 1) {
            event.element.style.backgroundColor = backgroundColor;
          } else {
            event.element.style.backgroundImage =
              this.strippedBackground(event);
          }
        }
        event.element.style['border-radius'] = '4px';

        if (
          this.masterCustomerId &&
          event.data.customer !== this.masterCustomerId
        ) {
          if (showCustomEvents && isCustomEvent) {
            event.element.style.opacity = '1';
          } else {
            event.element.style.opacity = '0.25';
          }
        } else if (isCustomEvent && !showCustomEvents) {
          event.element.style.opacity = '0.25';
        } else {
          event.element.style.opacity = '1';
        }
      }
    }
  }

  onTreeDragStop(event: DragAndDropEventArgs): void {
    event.cancel = true;

    let scheduleElement: Element = closest(event.target, '.e-content-wrap');

    if (scheduleElement) {
      let treeviewData = this.treeObj.fields.dataSource as {
        [key: string]: Object;
      }[];

      if (event.target.classList.contains('e-work-cells')) {
        const task = treeviewData.find((item: any) => {
          return item.id === parseInt(event.draggedNodeData.id as string, 10);
        });

        let cellData = this.scheduleObj.getCellDetails(event.target);

        let eventData = {
          ...task,
          Id: task.id,
          type: task.type,
          StartTime: cellData.startTime,
          EndTime: cellData.endTime,
          Draggable: true,
        };
        eventData[task.type as string] = task.id;

        this.scheduleObj.openEditor(eventData, 'Add', true);
        this.isTreeItemDropped = true;
      }
    }
  }

  onItemDrag(event: any): void {
    if (document.body.style.cursor === 'not-allowed') {
      document.body.style.cursor = '';
    }
  }

  dragSeriesByDefault = false;
  eventDragStart(event) {
    if (event.data?.IsRecurring) {
      this.dragSeriesByDefault = this.window.confirm(
        'Edit Series (All in this group) ?'
      );
    }
  }

  eventDragStop(event) {
    event.cancel = !this.window.confirm('Confirm new slot');
    if (event.cancel) {
      return;
    }

    if (event.data?.IsRecurring) {
      event.data.editMode = this.dragSeriesByDefault ? 'series' : 'current';
      this.dragSeriesByDefault = false;
      this.updateEvents$.next(event.data);
    } else if (event.data?.IsReminder) {
      this.reminderIsChanged(event);
    } else {
      this.updateEvents$.next(event.data);
    }
  }

  resizeStop(event): void {
    event.cancel = !this.window.confirm('Confirm new slot');
    if (event.cancel) {
      return;
    }

    if (event.data?.IsReminder) {
      this.reminderIsChanged(event);
    } else {
      this.updateEvents$.next(event.data);
    }
  }

  reminderIsChanged(event): void {
    event.data.reminderAddress = null;
    event.data.reminder = {
      description: event.data.Subject,
      technicians: event.data.TechnicianId,
      startDate: moment(event.data.startTime).toJSON(),
      endDate: moment(event.data.endTime).toJSON(),
    };
    event.data.reminderEvents = {};
    this.updateEvents$.next(event.data);
  }

  onCellClick(args): void {
    this.cellClick.emit(args);
    args.cancel = true;
  }

  treeViewCreated(): void {
    this.fetchWaitingTasks();
  }
}
